/**
 * Пользовательские строки интерфейса.
 *
 * Строки не пишутся в JSX напрямую: добавление второго языка не должно
 * требовать правки компонентов (NFR-U4). Полноценная библиотека локализации
 * появится, когда появится второй язык, — сейчас достаточно словаря.
 */

import { plural, withCount } from './plural'

export const strings = {
  appName: 'DB Trainer',
  nav: {
    catalog: 'Каталог',
    tracks: 'Треки',
    progress: 'Прогресс',
    login: 'Войти',
    register: 'Регистрация',
    logout: 'Выйти',
  },
  landing: {
    title: 'Тренируйте SQL на настоящей PostgreSQL',
    subtitle:
      'Задачи с автоматической проверкой, подсказками и разбором решения. Без установки — сразу в браузере.',
    cta: 'Начать бесплатно',
    features: [
      'Реальная база, а не эмулятор',
      'Подсказки по шагам, а не готовый ответ',
      'Разбор решения после каждой задачи',
    ],
  },
  catalog: {
    title: 'Каталог задач',
    empty: 'Задач пока нет',
    emptyHint: 'Ни одна задача не подошла под выбранные фильтры.',
    loading: 'Загружаем каталог…',
    loadFailed: 'Не удалось загрузить каталог',
    filters: {
      topic: 'Тема',
      difficulty: 'Сложность',
      status: 'Статус',
      tag: 'Метка',
      any: 'Любая',
      anyStatus: 'Любой',
      reset: 'Сбросить фильтры',
      statusRequiresLogin: 'Фильтр по статусу доступен после входа',
    },
    difficulty: {
      easy: 'простая',
      medium: 'средняя',
      hard: 'сложная',
    },
    // Статус дублируется текстом, а не только значком: цвет и форма не должны
    // быть единственным носителем смысла (docs/10-ui-ux.md, доступность).
    status: {
      solved: 'решено',
      solvedWithHints: 'решено с подсказками',
      review: 'пора повторить',
      attempted: 'есть попытки',
      notStarted: 'не начата',
    },
    solvedOfTotal: (solved: number, total: number) => `${solved} / ${total} решено`,
    tasksTotal: (total: number) => withCount(total, ['задача', 'задачи', 'задач']),
    attempts: (count: number) => withCount(count, ['попытка', 'попытки', 'попыток']),
  },
  tracks: {
    title: 'Учебные треки',
    subtitle: 'Задачи по порядку: от первого запроса до оконных функций',
    loading: 'Загружаем треки…',
    loadFailed: 'Не удалось загрузить треки',
    empty: 'Треков пока нет',
    notFound: 'Такого трека нет',
    backToTracks: '← Все треки',
    steps: (total: number) => withCount(total, ['шаг', 'шага', 'шагов']),
    stepsSolved: (solved: number, total: number) => `${solved} / ${total} пройдено`,
    start: 'Начать трек',
    resume: 'Продолжить',
    finished: 'Трек пройден целиком',
    stepNumber: (position: number) => `Шаг ${position}`,
    // Отметка «сюда идти дальше» дублируется текстом, а не только рамкой:
    // цвет и форма не должны быть единственным носителем смысла.
    nextStep: 'Следующий шаг',
    loginToTrack: 'Войдите, чтобы видеть свой прогресс по треку',
  },
  task: {
    loading: 'Загружаем задачу…',
    notFound: 'Задача не найдена',
    notFoundHint: 'Возможно, ссылка устарела или задача ещё не опубликована.',
    draftBadge: 'черновик',
    backToCatalog: 'К каталогу',
    expectedColumns: 'Колонки',
    orderMatters: 'Порядок строк важен',
    statementTitle: 'Условие',
    attemptsAndHints: (attempts: number, hints: number) =>
      `Попыток: ${attempts} · Подсказок: ${hints}`,
    guestNotice: 'Войдите, чтобы выполнять запросы и отправлять решения',
    guestAction: 'Войти',
  },
  hints: {
    title: 'Подсказки',
    locked: 'закрыта',
    // Раскрытая подсказка при возврате к задаче свёрнута: заголовок и пометка
    // видны, тело — по клику (FR-H9). Факт раскрытия этим не отменяется.
    revealedBadge: 'открыта',
    reveal: 'Открыть подсказку',
    // Предупреждение обязано быть до раскрытия, а не после: пользователь
    // выбирает между помощью и отметкой о самостоятельном решении (FR-H3).
    confirmTitle: (title: string) => `Открыть подсказку «${title}»?`,
    confirmWarning: 'Задача больше не будет отмечена как решённая самостоятельно.',
    confirm: 'Открыть',
    cancel: 'Отмена',
    revealing: 'Открываем…',
    previousFirst: 'Сначала откройте предыдущую подсказку',
    revealFailed: 'Не удалось открыть подсказку. Попробуйте ещё раз.',
    guestNotice: 'Войдите, чтобы открывать подсказки',
  },
  solution: {
    reveal: 'Показать решение',
    confirmTitle: 'Показать решение задачи?',
    confirmWarning:
      'Задача будет отмечена как решённая с подсказкой решения. Разбор откроется сразу.',
    confirm: 'Показать решение',
    cancel: 'Отмена',
    revealing: 'Открываем…',
    revealFailed: 'Не удалось открыть решение. Попробуйте ещё раз.',
    revealed: 'Решение раскрыто — разбор доступен под результатом',
  },
  explanation: {
    title: 'Разбор',
    loading: 'Загружаем разбор…',
    loadFailed: 'Не удалось загрузить разбор',
    referenceTitle: 'Эталонное решение',
    nextInTopic: 'Следующая задача в теме →',
    lastInTopic: 'Это последняя задача темы',
    nextInTrack: 'Следующий шаг трека →',
    lastInTrack: 'Это последний шаг трека',
    backToTrack: 'Вернуться к треку',
    backToCatalog: 'Выбрать следующую задачу',
  },
  submissions: {
    title: 'Мои попытки',
    empty: 'Отправок по этой задаче ещё не было',
    loading: 'Загружаем историю…',
    loadFailed: 'Не удалось загрузить историю отправок',
    insert: 'Подставить этот запрос в редактор',
    status: {
      passed: 'верно',
      wrong_answer: 'не принято',
      sql_error: 'ошибка SQL',
      timeout: 'таймаут',
      forbidden: 'запрос не принят',
    },
  },
  schema: {
    title: (dataset: string) => `Схема: ${dataset}`,
    loading: 'Загружаем схему…',
    loadFailed: 'Не удалось загрузить схему датасета',
    rows: (count: number) => withCount(count, ['строка', 'строки', 'строк']),
    primaryKey: 'первичный ключ',
    foreignKey: (reference: string) => `ссылается на ${reference}`,
    nullable: 'может быть NULL',
    showData: 'Показать данные',
    hideData: 'Скрыть данные',
    previewLoading: 'Читаем строки…',
    previewFailed: 'Не удалось прочитать данные таблицы',
    insertColumn: (name: string) => `Вставить «${name}» в запрос`,
  },
  editor: {
    label: 'Редактор SQL',
    run: 'Выполнить',
    runHint: 'Ctrl/⌘ + Enter',
    submit: 'Проверить',
    submitHint: 'Ctrl/⌘ + Shift + Enter',
    explain: 'Показать план',
    explaining: 'Строим план…',
    running: 'Выполняется…',
    placeholder: 'Напишите запрос',
    draftRestored: 'Восстановлен черновик',
  },
  result: {
    empty: 'Здесь появится результат запроса',
    heading: (rows: number, ms: number) =>
      `Результат · ${withCount(rows, ['строка', 'строки', 'строк'])} · ${ms} мс`,
    passed: 'Верно!',
    passedFirst: 'Верно! Задача решена.',
    wrongAnswer: 'Решение пока не принято',
    orderMismatch: 'Почти: набор строк верный, но порядок другой',
    sqlError: 'PostgreSQL не выполнил запрос',
    timeout: 'Запрос не уложился в отведённое время',
    forbidden: 'Запрос не принят',
    unavailable: 'Задача временно недоступна',
    unavailableHint: 'Обновляется эталонное решение — попробуйте позже.',
    truncated: (limit: number) =>
      `Показаны первые ${withCount(limit, ['строка', 'строки', 'строк'])} — результат усечён`,
    errorPosition: (line: number, column: number) => `строка ${line}, символ ${column}`,
    extraRows: 'Строки из вашего результата, которых нет в ответе:',
    noRows: 'Запрос не вернул ни одной строки',
    retry: 'Повторить',
    busy: 'Сервис занят, попробуйте через несколько секунд',
    rateLimited: (seconds: number) => `Слишком много запусков. Повторите через ${seconds} с`,
  },
  // План запроса (FR-T15). Инструмент, а не проверка: ничего не оценивается.
  plan: {
    heading: (ms: number) => `План запроса · ${ms} мс`,
    // Без этой оговорки `Seq Scan` на сотне строк читается как «ошибка»,
    // хотя планировщик прав: перебор маленькой таблицы дешевле индекса.
    estimateNotice:
      'Это план, который построил PostgreSQL: как он собирается выполнить запрос ' +
      'и сколько строк ожидает. Числа — оценка планировщика, а не факт: ' +
      'запрос не выполнялся.',
    rows: (count: number) => `≈ ${withCount(count, ['строка', 'строки', 'строк'])}`,
    cost: (value: number) => `стоимость ${value}`,
    onRelation: (name: string) => `по таблице ${name}`,
    byIndex: (name: string) => `индекс ${name}`,
    details: 'Показать целиком',
    detailsHide: 'Свернуть',
    // Доступное имя кнопки: подряд их столько же, сколько узлов в плане,
    // и без типа узла диктор читает одно и то же слово много раз.
    detailsFor: (nodeType: string) => `Показать целиком: ${nodeType}`,
    detailsHideFor: (nodeType: string) => `Свернуть: ${nodeType}`,
    empty: 'PostgreSQL не вернул план',
  },
  // Задачи на изменение данных (FR-T14, docs/adr/0006-dml-tasks.md).
  changes: {
    title: 'Что изменилось',
    rowsAffected: (rows: number) => `изменено ${withCount(rows, ['строка', 'строки', 'строк'])}`,
    failed: 'не сошлось',
    // Заголовок блока результата у пробного запуска DML-задачи: строк такой
    // запрос не возвращает, и «Результат · 0 строк» вводило бы в заблуждение.
    heading: (rows: number, ms: number) =>
      `Запрос выполнен · изменено ${withCount(rows, ['строка', 'строки', 'строк'])} · ${ms} мс`,
    committed: 'Изменения фиксируются, затем копия данных восстанавливается',
    rolledBack: 'Изменения откатываются сразу после проверки',
    whatIsChecked: 'Что будет проверено',
    noSideEffect: 'и что остальные данные не изменились',
    sandbox:
      'Задача решается на вашей личной копии данных: она восстанавливается ' +
      'после каждой отправки, испортить её нельзя.',
  },
  auth: {
    loginTitle: 'Вход',
    registerTitle: 'Регистрация',
    email: 'Email',
    password: 'Пароль',
    displayName: 'Имя (необязательно)',
    passwordRequirements: 'От 8 до 128 символов. Простые и часто используемые пароли не подойдут.',
    // Прямое следствие отказа от подтверждающих кодов в MVP (ADR-0005):
    // умолчать о том, что забытый пароль означает потерю аккаунта, нельзя.
    noRecoveryWarning:
      'Восстановление пароля по email пока не работает — сохраните пароль в менеджере паролей.',
    submitLogin: 'Войти',
    submitRegister: 'Создать аккаунт',
    pending: 'Отправляем…',
    noAccount: 'Нет аккаунта?',
    haveAccount: 'Уже есть аккаунт?',
    goToRegister: 'Зарегистрироваться',
    goToLogin: 'Войти',
    sessionExpired: 'Сессия истекла, войдите заново',
    unknownError: 'Не удалось выполнить запрос. Попробуйте ещё раз.',
    checking: 'Проверяем сессию…',
    // Федеративный вход (ADR-0008)
    continueWith: (provider: string) => `Продолжить через ${provider}`,
    orDivider: 'или по email и паролю',
    providerNames: { github: 'GitHub', google: 'Google' },
    oauthFailed: {
      oauth_declined: 'Вход через провайдера отменён',
      oauth_state_invalid: 'Сессия входа устарела — попробуйте ещё раз',
      oauth_exchange_failed: 'Провайдер не ответил. Попробуйте ещё раз через минуту',
      oauth_provider_unknown: 'Этот способ входа недоступен',
      oauth_email_missing:
        'Провайдер не сообщил адрес почты. Откройте адрес в его настройках или войдите по паролю',
      // Провайдер не подтвердил адрес, а аккаунт с таким адресом уже есть:
      // связать автоматически нельзя, иначе это захват чужого аккаунта.
      email_taken:
        'Аккаунт с таким адресом уже есть. Войдите по паролю и привяжите провайдера в профиле',
      identity_already_linked: 'Этот аккаунт провайдера уже привязан к другому пользователю',
      account_disabled: 'Аккаунт заблокирован',
    } as Record<string, string>,
    oauthUnknownError: 'Войти через провайдера не удалось. Попробуйте ещё раз',
  },
  profile: {
    title: 'Профиль',
    displayName: 'Имя',
    noDisplayName: 'не задано',
    registeredAt: 'Регистрация',
    changePassword: 'Смена пароля',
    changePasswordHint: 'После смены пароля вход на других устройствах потребуется заново.',
    currentPassword: 'Текущий пароль',
    newPassword: 'Новый пароль',
    submitPassword: 'Сменить пароль',
    passwordChanged: 'Пароль изменён',
    // Способы входа (FR-A13)
    setPassword: 'Пароль',
    setPasswordHint:
      'У аккаунта пока нет пароля — вход только через провайдера. Задайте пароль, чтобы входить и по email.',
    submitSetPassword: 'Задать пароль',
    loginMethods: 'Способы входа',
    loginMethodsLoading: 'Загружаем способы входа…',
    passwordMethod: 'Пароль',
    passwordMethodOn: 'задан',
    passwordMethodOff: 'не задан',
    linkedAt: (date: string) => `привязан ${date}`,
    linkProvider: 'Привязать',
    unlinkProvider: 'Отвязать',
    providerLinked: (provider: string) => `${provider} привязан`,
    lastMethodHint: 'Это единственный способ входа: сначала задайте пароль.',
  },
  progress: {
    title: 'Мой прогресс',
    loading: 'Считаем прогресс…',
    loadFailed: 'Не удалось загрузить прогресс',
    solvedOfTotal: (solved: number, total: number) => `Решено ${solved} из ${total}`,
    // То, чем приятно гордиться: задача, решённая без подсказок и разбора
    // (docs/10-ui-ux.md).
    solvedWithoutHints: 'Из них самостоятельно',
    attempted: 'В работе',
    byTopic: 'По темам',
    byTrack: 'По трекам',
    byDifficulty: 'По сложности',
    recentSolved: 'Недавно решённые',
    recentEmpty: 'Пока ни одной решённой задачи — самое время начать',
    goToCatalog: 'К каталогу задач',
    solvedAt: (date: string) => `решено ${date}`,
    // Долг на самостоятельное решение (FR-P8). Формулировки приглашают,
    // а не укоряют: подсказка — не проступок, и счётчик за неё не падает.
    reviewSection: (count: number) => `Ждут самостоятельного решения (${count})`,
    reviewClosed: 'Долгов закрыто',
    reviewDueAt: (date: string) => `ждёт с ${date}`,
  },
  review: {
    notice: 'В прошлый раз вы решали эту задачу с подсказками. Попробуем без них?',
    clearEditor: 'Очистить редактор',
    dismiss: 'Больше не напоминать',
    dismissFailed: 'Не удалось убрать напоминание',
    // Согласование по числу: «1 шаг ждёт», но «3 шага ждут».
    stepsWaiting: (count: number) =>
      `${withCount(count, ['шаг', 'шага', 'шагов'])} ${plural(count, ['ждёт', 'ждут', 'ждут'])} самостоятельного решения`,
  },
  notFound: {
    title: 'Страница не найдена',
    action: 'На главную',
  },
  theme: {
    label: 'Тема оформления',
    light: 'Светлая',
    dark: 'Тёмная',
    system: 'Системная',
  },
  service: {
    versionUnknown: 'версия неизвестна',
    apiUnreachable: 'API недоступен',
  },
} as const
